import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@akuna/ui";
import { useTranslation } from "react-i18next";
import { useStayPlans, type StayPlan } from "@/hooks/useStayPlans";
import { AddTile, SectionCard, SectionHeader } from "../accommodations/primitives";
import StayPlanCard from "./StayPlanCard";
import StayPlanDialog from "./StayPlanDialog";

export default function StayPlansSection({
  canCreate,
  canEdit,
  canDelete,
}: {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}) {
  const { t, i18n } = useTranslation("daycareHotel");
  const { data: plans, isLoading } = useStayPlans("daycare");
  const [editing, setEditing] = useState<StayPlan | null>(null);
  const [open, setOpen] = useState(false);
  const money = (value: number) =>
    new Intl.NumberFormat(i18n.language.startsWith("pt") ? "pt-BR" : "en-US", { style: "currency", currency: "BRL" }).format(value);
  const start = (plan: StayPlan | null) => {
    setEditing(plan);
    setOpen(true);
  };
  const recurringPlans = (plans ?? []).filter((plan) => plan.kind === "recurring");

  return (
    <section>
      <SectionHeader
        tone="daycare"
        badge={t("plans.daycareBadge")}
        title={t("plans.title")}
        description={t("plans.description")}
        action={canCreate ? (
          <Button size="sm" variant="outline" onClick={() => start(null)}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t("plans.new")}
          </Button>
        ) : undefined}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? [0, 1].map((key) => <SectionCard key={key} className="h-40 animate-pulse bg-muted/40" />)
          : recurringPlans.map((plan) => (
              <StayPlanCard key={plan.id} plan={plan} canEdit={canEdit} canDelete={canDelete} onEdit={() => start(plan)} money={money} />
            ))}
        {canCreate ? <AddTile onClick={() => start(null)} tone="daycare" minHeight="160px" icon={<Plus className="h-5 w-5" />} title={t("plans.new")} /> : null}
      </div>
      <StayPlanDialog open={open} onOpenChange={setOpen} kind="recurring" allowedModules={["daycare"]} plan={editing} />
    </section>
  );
}
